import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { Trans, useTranslation } from "react-i18next";
import { SPOTButton } from "spot-component-library/lib/components/SPOTButton";
import { Panel } from "spot-component-library/lib/components/basic/panel/Panel";
import { Spinner } from "spot-component-library/lib/components/basic/spinner/Spinner";
import { SPOTCheckBox } from "spot-component-library/lib/components/form/checkbox/SPOTCheckBox";
import { SPOTRadioButton } from "spot-component-library/lib/components/form/radioButton/SPOTRadioButton";
import { SPOTInput } from "spot-component-library/lib/components/form/spotInput/SPOTInput";
import { SPOTNotification } from "spot-component-library/lib/components/notification/SPOTNotification";
import PaypalLogo from "../../../resources/images/Paypal_Logo.svg";
import CreditCardLogo from "../../../resources/images/creditcard.png";
import SepaLogo from "../../../resources/images/SEPA_Logo.svg";
import ue_de from "../../../resources/images/Überweisung_Logo_de.svg";
import ue_en from "../../../resources/images/Überweisung_Logo_en.svg";
import ue_fr from "../../../resources/images/Überweisung_Logo_fr.svg";
import ue_it from "../../../resources/images/Überweisung_Logo_it.svg";
import { VerifyUser } from "../../components/verifyUser/VerifyUser";
import debtService from "../../services/DebtService";
import paymentService from "../../services/PaymentService";
import rateAgreementService from "../../services/RateAgreementService";
import utilService from "../../services/UtilService";
import { BankTransferOptions } from "../../types/BankTransferOptions";
import { Company } from "../../types/Company";
import { Debtor } from "../../types/Debtor";
import { PaymentFormData } from "../../types/PaymentFormData";
import { PaymentType } from "../../types/PaymentType";
import { RateData } from "../../types/RateData";
import { useRateAgreementTenantConfiguration } from "../../contexts/TenantContext";
import "./PaymentForm.scss";
import i18next from "i18next";

interface CheckBoxes {
    ibanMandat?: boolean;
    ibanMandatPost?: boolean;
    returnDebitInfo?: boolean;
    optIn?: boolean;
}

type PaymentFormProps = {
    paymentForm: PaymentFormData;
    rateData: Required<RateData>;
    onBack: (paymentForm: PaymentFormData) => void;
    onNext: (paymentForm: PaymentFormData) => void;
};

export const PaymentForm = (props: PaymentFormProps) => {
    const { debtNumber, rateAmount } = props.rateData;

    const { t } = useTranslation();
    const rateAgreementConfig = useRateAgreementTenantConfiguration();
    const [paymentForm, setPaymentForm] = useState<PaymentFormData>(props.paymentForm);
    const [disablePayments, setDisablePayments] = React.useState(true);
    const [checkBoxes, setCheckBoxes] = React.useState<CheckBoxes>({});
    const [selectedPaymentMethod, setSelectedPaymentMethod] = React.useState<string>("");
    const [bankTransferOptions, setBankTransferOptions] = useState<BankTransferOptions | null>();
    const [isPageLoaded, setIsPageLoaded] = React.useState<boolean>(false);
    const [allowedPaymentTypes, setAllowedPaymentTypes] = React.useState<PaymentType[]>([]);
    const [company, setCompany] = React.useState<Company | null>();

    const {
        register,
        trigger,
        reset,
        formState: { errors, isValid },
    } = useForm({
        mode: "all",
    });

    React.useEffect(() => {
        initData()
            .then(() => setIsPageLoaded(true))
            .catch(() => setIsPageLoaded(true));
    }, []);

    React.useEffect(() => {
        if (rateAgreementConfig !== undefined) {
            setDisablePayments(rateAgreementConfig?.enableUserNameVerificationForRateagreementCreation);
        }
    }, [rateAgreementConfig?.enableUserNameVerificationForRateagreementCreation]);

    async function initData() {
        if (!debtNumber) return;

        try {
            const [companyResponse, bankTransferOptionsResponse, allowedPaymentTypesResponse] = await Promise.all([
                handlePromise(debtService.getCompany(debtNumber)),
                handlePromise(rateAgreementService.getBankTransferOptions(debtNumber)),
                handlePromise(paymentService.getAllowedPaymentTypes(debtNumber, rateAmount)),
            ]);

            setCompany(companyResponse);
            setBankTransferOptions(bankTransferOptionsResponse);
            setAllowedPaymentTypes(determineAllowedPaymentTypes(bankTransferOptionsResponse, allowedPaymentTypesResponse || []));
        } catch (error) {
            console.error("Error initializing data:", error);
        }
    }

    const changeHandler = (e: any) => {
        const target = e.target;
        const name = target.name;
        let value = target.value;
        const checked = target.checked;
        if (checked) {
            value = checked;
        }
        const paymentFormTmp: PaymentFormData = { ...paymentForm };
        paymentFormTmp[name as keyof PaymentFormData] = value;
        setPaymentForm(paymentFormTmp);
    };

    const isIbanValid = async (value: string) => {
        if (paymentForm.iban) {
            try {
                const valid = await utilService.isIbanValid(value);
                if (!valid) {
                    return t("APP_ROOT.VALIDATION.IBAN_INVALID");
                }
            } catch (e) {
                return t("APP_ROOT.VALIDATION.IBAN_CANT_NOT_BE_VERIFIED");
            }
        }
        return true;
    };

    // handler for all checkboxes
    const checkBoxesHandler = ({ target }: React.ChangeEvent<HTMLInputElement>) => {
        const name = target.name as keyof CheckBoxes;
        const checked = target.checked;

        setCheckBoxes({ ...checkBoxes, [name]: checked });

        if (name === "ibanMandat") {
            setPaymentForm({ ...paymentForm, ibanMandat: checked });
        }
    };

    const handleRadioButtonEvt = ({ target: activeRadioBtn }: React.ChangeEvent<HTMLInputElement>) => {
        reset();

        if (!activeRadioBtn.value) {
            setSelectedPaymentMethod("");
            return;
        }

        if (activeRadioBtn.id === "sepa-radio-btn") {
            setSelectedPaymentMethod(activeRadioBtn.value);
            setPaymentForm({
                ...paymentForm,
                kreditinstitut: "",
                iban: "",
                paymentType: activeRadioBtn.value,
            });
            return;
        }

        setCheckBoxes({ ...checkBoxes, ibanMandat: false });
        setSelectedPaymentMethod(activeRadioBtn.value);

        setPaymentForm({
            ...paymentForm,
            paymentType: activeRadioBtn.value,
            kontoinhaber: bankTransferOptions?.debtCompanyAccountOwner,
            kreditinstitut: bankTransferOptions?.bankName,
            iban: bankTransferOptions?.iban,
        });
    };

    const isActive = (): boolean => {
        const isDirectDebit = paymentForm.paymentType === PaymentType.ABO_DIRECTDEBIT;
        const isKontoinhaberSet = Boolean(paymentForm.kontoinhaber?.trim());

        return isValid && selectedPaymentMethod !== "" && !disablePayments && (!isDirectDebit || isKontoinhaberSet);
    };

    const handleUserVerified = (debtor: Debtor | null) => {
        if (debtor) {
            const { firstName: verifiedFirstName, lastName: verifiedLastName } = debtor;

            setPaymentForm({
                ...paymentForm,
                kontoinhaber: `${verifiedFirstName} ${verifiedLastName}`, // need to be full name, same as stored in debt collect system returned in verify response (exl. one first name ist not enough)
                optIn: true,
                optInDate: new Date(),
                optInFirstName: verifiedFirstName,
                optInLastName: verifiedLastName,
            });

            trigger("kontoinhaber").then(() => trigger("kontoinhaber"));
            setDisablePayments(false);
        } else {
            setDisablePayments(true);
        }
    };

    return (
        <>
            <div data-test="paymentForm" id={"paymentForm"}>
                <Panel>
                    {isPageLoaded && (
                        <>
                            {rateAgreementConfig?.enableUserNameVerificationForRateagreementCreation && (
                                <div className={"optIn"}>
                                    <VerifyUser
                                        debtNumber={debtNumber}
                                        onVerifyResult={handleUserVerified}
                                        defaultValue={{
                                            firstName: paymentForm.optInFirstName,
                                            lastName: paymentForm.optInLastName,
                                        }}
                                    />
                                </div>
                            )}
                            <div className="paymentMethods">
                                <b>
                                    <p className={"payment-methods-title"}>
                                        <Trans i18nKey="PAYMENT_PAGE.Headline" values={{ rateAmount }} />
                                    </p>
                                </b>

                                {isPaymentAvailable(allowedPaymentTypes, PaymentType.ABO_PAYPAL) && (
                                    <SPOTRadioButton
                                        groupName={"payment-method"}
                                        id={"paypal-radio-btn"}
                                        testId={"paypal-radio-btn"}
                                        value={PaymentType.ABO_PAYPAL}
                                        checked={selectedPaymentMethod === PaymentType.ABO_PAYPAL}
                                        logo={PaypalLogo}
                                        disabled={disablePayments}
                                        elementLabel={t("APP_ROOT.PAYMENT_TYPE.PAYPAL")}
                                        onChange={handleRadioButtonEvt}
                                    />
                                )}

                                {isPaymentAvailable(allowedPaymentTypes, PaymentType.ABO_CREDITCARD) && (
                                    <SPOTRadioButton
                                        groupName={"payment-method"}
                                        id={"creditcard-radio-btn"}
                                        testId={"creditcard-radio-btn"}
                                        value={PaymentType.ABO_CREDITCARD}
                                        checked={selectedPaymentMethod === PaymentType.ABO_CREDITCARD}
                                        logo={CreditCardLogo}
                                        disabled={disablePayments}
                                        elementLabel={t("APP_ROOT.PAYMENT_TYPE.CREDITCARD")}
                                        onChange={handleRadioButtonEvt}
                                    />
                                )}

                                {isPaymentAvailable(allowedPaymentTypes, PaymentType.ABO_DIRECTDEBIT) && (
                                    <>
                                        <SPOTRadioButton
                                            groupName={"payment-method"}
                                            id={"sepa-radio-btn"}
                                            testId={"sepa-radio-btn"}
                                            value={PaymentType.ABO_DIRECTDEBIT}
                                            checked={selectedPaymentMethod === PaymentType.ABO_DIRECTDEBIT}
                                            logo={SepaLogo}
                                            disabled={disablePayments}
                                            elementLabel={t("APP_ROOT.PAYMENT_TYPE.DIRECT_DEBIT")}
                                            onChange={handleRadioButtonEvt}
                                        />

                                        {selectedPaymentMethod === PaymentType.ABO_DIRECTDEBIT && (
                                            <div className={"lsvBlock"} data-test="sepaBlock">
                                                <div className={"lsvDataBlock"} data-test="sepaDataBlock">
                                                    <p>
                                                        <b>{t("PAYMENT_PAGE.HeadlineSepa")}</b>
                                                    </p>
                                                    <SPOTInput
                                                        id={"kreditinstitut"}
                                                        name={"kreditinstitut"}
                                                        label={t("PAYMENT_PAGE.Kreditinstitut")}
                                                        errors={errors}
                                                        onChange={changeHandler}
                                                        register={register}
                                                        required={true}
                                                        pattern={{
                                                            value: /^[^=<>&?%(){};~|+'´`"\\]+$/,
                                                            message: t("APP_ROOT.VALIDATION.MESSAGE_LETTERS"),
                                                        }}
                                                        defaultValue={paymentForm.kreditinstitut}
                                                    />
                                                    <SPOTInput
                                                        id={"kontoinhaber"}
                                                        name={"kontoinhaber"}
                                                        label={t("PAYMENT_PAGE.Kontoinhaber")}
                                                        errors={errors}
                                                        onChange={changeHandler}
                                                        disabled={true}
                                                        register={register}
                                                        pattern={{
                                                            value: /^[^=<>&?%(){};~|+'´`"\\]+$/,
                                                            message: t("APP_ROOT.VALIDATION.MESSAGE_LETTERS"),
                                                        }}
                                                        defaultValue={paymentForm.optInFirstName + " " + paymentForm.optInLastName}
                                                    />
                                                    <SPOTInput
                                                        id={"iban"}
                                                        name={"iban"}
                                                        label={t("PAYMENT_PAGE.Iban")}
                                                        maxLength={22}
                                                        onChange={changeHandler}
                                                        errors={errors}
                                                        register={register}
                                                        required={true}
                                                        pattern={{
                                                            value: /^DE[A-Z0-9]{20}$/,
                                                            message: t("APP_ROOT.VALIDATION.MESSAGE_IBAN"),
                                                        }}
                                                        defaultValue={paymentForm.iban}
                                                        computedValidation={isIbanValid}
                                                    />

                                                    <div className="privacyBox" data-test="ibanMandat">
                                                        <SPOTCheckBox
                                                            id="ibanMandatCheckBox"
                                                            name="ibanMandat"
                                                            checked={Boolean(checkBoxes.ibanMandat)}
                                                            register={register}
                                                            label={t("PAYMENT_PAGE.Mandat", {
                                                                company: company?.companyName,
                                                                address: `${company?.streetAndHousnumber}, ${company?.postCodeAndCity}`,
                                                            })}
                                                            onChange={checkBoxesHandler}
                                                            required={t("APP_ROOT.VALIDATION.MESSAGE_REQUIRED") as string}
                                                            errors={errors}
                                                        />
                                                        <br />

                                                        <SPOTCheckBox
                                                            id="ibanMandatPostCheckBox"
                                                            name="ibanMandatPost"
                                                            checked={Boolean(checkBoxes.ibanMandatPost)}
                                                            register={register}
                                                            label={t("PAYMENT_PAGE.MandantPost")}
                                                            onChange={checkBoxesHandler}
                                                            required={t("APP_ROOT.VALIDATION.MESSAGE_REQUIRED") as string}
                                                            errors={errors}
                                                        />

                                                        <br />

                                                        <SPOTCheckBox
                                                            id="returnDebitInfoCheckBox"
                                                            name="returnDebitInfo"
                                                            checked={Boolean(checkBoxes.returnDebitInfo)}
                                                            register={register}
                                                            label={
                                                                <Trans
                                                                    i18nKey="PAYMENT_PAGE.ReturnDebitInfo"
                                                                    components={{
                                                                        bold: <b />,
                                                                    }}
                                                                />
                                                            }
                                                            onChange={checkBoxesHandler}
                                                            required={t("APP_ROOT.VALIDATION.MESSAGE_REQUIRED") as string}
                                                            errors={errors}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </>
                                )}

                                {isPaymentAvailable(allowedPaymentTypes, PaymentType.ABO_BANKTRANSFER) && (
                                    <SPOTRadioButton
                                        groupName={"payment-method"}
                                        id={"bank-transfer-radio-btn"}
                                        testId={"bank-transfer-radio-btn"}
                                        value={PaymentType.ABO_BANKTRANSFER}
                                        checked={selectedPaymentMethod === PaymentType.ABO_BANKTRANSFER}
                                        disabled={disablePayments}
                                        logo={
                                            i18next.language === "de"
                                                ? ue_de
                                                : i18next.language === "en"
                                                  ? ue_en
                                                  : i18next.language === "fr"
                                                    ? ue_fr
                                                    : i18next.language === "it"
                                                      ? ue_it
                                                      : ue_de
                                        }
                                        onChange={handleRadioButtonEvt}
                                    />
                                )}

                                {noPaymentAvailable(allowedPaymentTypes) && (
                                    <div className={"no-abo-payments"}>
                                        <SPOTNotification text={t("PAYMENT_PAGE.NO_PAYMENTS_ERROR")} active={true} type={"error"} />
                                    </div>
                                )}
                            </div>

                            <p className={"position-required-hint"}>{t("PAYMENT_PAGE.Pflichtfeld")}</p>
                        </>
                    )}
                    <Spinner isLoading={!isPageLoaded} type="page" color={"grey"} />
                </Panel>
            </div>
            <div className={"buttonBlock"}>
                <SPOTButton id={"buttonBackToRateCalculation"} type="secondary" label={t("PAYMENT_PAGE.Zurueck")} onClick={() => props.onBack(paymentForm)} />
                <SPOTButton id={"continueToRatePlanSummary"} type="primary" label={t("PAYMENT_PAGE.Weiter")} onClick={() => props.onNext(paymentForm)} disabled={!isActive()} />
            </div>
        </>
    );
};

export async function handlePromise<T>(promise: Promise<T>) {
    try {
        return await promise;
    } catch (error) {
        console.error("Promise failed:", error);
        return null;
    }
}

export function determineAllowedPaymentTypes(bankTransferOptionsResponse: BankTransferOptions | null, allowedPaymentTypesResponse: PaymentType[]) {
    const allowedPaymentTypes = [...allowedPaymentTypesResponse];

    try {
        if (!bankTransferOptionsResponse) {
            return allowedPaymentTypes.filter((paymentType) => paymentType !== PaymentType.ABO_BANKTRANSFER);
        }

        if (noPaymentAvailable(allowedPaymentTypes)) {
            allowedPaymentTypes.push(PaymentType.ABO_BANKTRANSFER);
        }

        return allowedPaymentTypes;
    } catch {
        return bankTransferOptionsResponse ? [PaymentType.ABO_BANKTRANSFER] : [];
    }
}

export function isPaymentAvailable(allowedPaymentTypes: PaymentType[], payment: PaymentType): boolean {
    return allowedPaymentTypes.includes(payment);
}

export function noPaymentAvailable(paymentTypes: PaymentType[]): boolean {
    return !paymentTypes || paymentTypes.length === 0;
}
